/**
 * Channel Reconciliation
 *
 * Single source of truth that joins:
 *   • Canonical Growth Channels (GROWTH_CHANNELS)
 *   • Lead Source → Channel mapping (canonical mapping)
 *   • Live Opportunities  (leads, appointments, won, net)
 *   • Live Jobs           (revenue)
 *   • Lead Intake records (valid lead %, integrity)
 *
 * The Cockpit Growth Channels page consumes ChannelRollup[] so every
 * outlet — Meta, Google, Angi, Thumbtack, Referral, plus an Unattributed
 * bucket — is shown with the SAME numbers used by Lead Sources and
 * Lead Integrity. No more divergent seed data.
 */
import { GROWTH_CHANNELS, type GrowthChannelDef } from "./growthChannelMapping";
import { DEFAULT_SOURCE_CHANNEL_MAP } from "./growthChannelMapping";
import { isNetWon, isPendingRescission } from "@/data/opportunityTypes";
import type { Opportunity } from "@/data/opportunityTypes";
import type { Job } from "@/data/types";
import type { IntakeRecord } from "@/data/leadIntegrityEngine";
import { getJobDisplayValue } from "@/data/jobPricingSelectors";

export interface ChannelRollup {
  /** Stable id — matches GrowthChannelDef.id, or "unattributed". */
  id: string;
  name: string;
  type: GrowthChannelDef["type"] | "derived";
  /** Lead source names that map to this channel. */
  sources: string[];
  /** Active in the platform (managed/tracked). */
  active: boolean;

  // ── Volume ──
  leads: number;
  appointments: number;
  wonGross: number;
  wonNet: number;
  pending: number;

  // ── Quality ──
  validLeadPct: number;        // from Lead Integrity intake records
  outOfAreaPct: number;
  intakeCount: number;         // intake records joined to this channel

  // ── Money ──
  monthlySpend: number;        // current rate
  revenue: number;             // contract value of jobs sourced here

  // ── Derived metrics ──
  cpl: number;                 // spend / leads
  cpb: number;                 // spend / appointments
  cpaGross: number;            // spend / wonGross
  cpaNet: number;              // spend / wonNet  ← the canonical CPA
  netConversionPct: number;    // wonNet / leads × 100
  roi: number;                 // (revenue - spend) / spend × 100
}

const UNATTRIBUTED_LABEL = "Unattributed";

function normalizeSource(s: string | undefined): string {
  return (s || "").trim();
}

/** Resolve which channel id a lead source belongs to (null = unattributed). */
function resolveChannelId(source: string | undefined): string | null {
  const s = normalizeSource(source);
  if (!s) return null;
  if (s in DEFAULT_SOURCE_CHANNEL_MAP) return DEFAULT_SOURCE_CHANNEL_MAP[s] ?? null;
  // Fuzzy fallback
  const lower = s.toLowerCase();
  if (lower.includes("facebook") || lower.includes("instagram") || lower.includes("meta")) return "meta-growth-engine";
  if (lower.includes("google")) return "google-ads";
  if (lower.includes("angi") || lower.includes("homeadvisor")) return "angi";
  if (lower.includes("thumbtack")) return "thumbtack";
  if (lower.includes("referral")) return "referral-engine";
  return null;
}

function isFinalizedJob(job: Job): boolean {
  const stage = String(job.currentStage ?? "").trim().toLowerCase();
  const closeout = String(job.closeoutStatus ?? "").trim().toLowerCase();
  return stage === "completed" || closeout === "closed";
}

export interface ReconcileInput {
  opportunities: Opportunity[];
  jobs: Job[];
  intakeRecords?: IntakeRecord[];
  spendOverrides?: Record<string, number>;
  /** Toggleable channels — id → active. Defaults to all on. */
  active?: Record<string, boolean>;
}

export function reconcileChannels(input: ReconcileInput): ChannelRollup[] {
  const { opportunities, jobs, intakeRecords = [], spendOverrides = {}, active = {} } = input;
  const netWonJobIds = new Set<string>();

  // 1. Initialize one bucket per canonical channel + Unattributed
  const buckets = new Map<string, ChannelRollup>();
  const init = (id: string, name: string, type: ChannelRollup["type"], isActive: boolean): ChannelRollup => ({
    id, name, type, sources: [], active: isActive,
    leads: 0, appointments: 0, wonGross: 0, wonNet: 0, pending: 0,
    validLeadPct: 0, outOfAreaPct: 0, intakeCount: 0,
    monthlySpend: spendOverrides[id] ?? 0,
    revenue: 0, cpl: 0, cpb: 0, cpaGross: 0, cpaNet: 0, netConversionPct: 0, roi: 0,
  });

  for (const ch of GROWTH_CHANNELS) {
    buckets.set(ch.id, init(ch.id, ch.name, ch.type, active[ch.id] ?? true));
  }
  buckets.set("unattributed", init("unattributed", UNATTRIBUTED_LABEL, "derived", true));

  // 2. Collect lead-source names per channel
  const sourcesByChannel = new Map<string, Set<string>>();
  for (const [src, chId] of Object.entries(DEFAULT_SOURCE_CHANNEL_MAP)) {
    const id = chId ?? "unattributed";
    if (!sourcesByChannel.has(id)) sourcesByChannel.set(id, new Set());
    sourcesByChannel.get(id)!.add(src);
  }

  // 3. Roll up opportunities
  for (const opp of opportunities) {
    const id = resolveChannelId(opp.leadSource) ?? "unattributed";
    const bucket = buckets.get(id);
    if (!bucket) continue;
    if (opp.leadSource) {
      const set = sourcesByChannel.get(id) ?? new Set<string>();
      set.add(opp.leadSource);
      sourcesByChannel.set(id, set);
    }
    bucket.leads += 1;
    if (opp.stage !== "Lead") bucket.appointments += 1;
    if (opp.stage === "Closed Won") bucket.wonGross += 1;
    if (isNetWon(opp)) {
      bucket.wonNet += 1;
      const linkedJobId = typeof opp.linkedJobId === "string" ? opp.linkedJobId.trim() : "";
      if (linkedJobId) netWonJobIds.add(linkedJobId);
    }
    if (isPendingRescission(opp)) bucket.pending += 1;
    const leadCost = Number(
      (opp as { leadCost?: number; lead_cost?: number }).leadCost ??
      (opp as { leadCost?: number; lead_cost?: number }).lead_cost ??
      0,
    );
    if (leadCost > 0) bucket.monthlySpend += leadCost;
  }

  // 4. Revenue from jobs
  for (const job of jobs) {
    const id = resolveChannelId(job.marketing?.leadSource) ?? "unattributed";
    const bucket = buckets.get(id);
    if (!bucket) continue;
    bucket.revenue += getJobDisplayValue(job);
    const leadCost = Number(job.marketing?.leadCost ?? 0);
    if (leadCost > 0) bucket.monthlySpend += leadCost;

    // Backstop: when closed jobs exist but opportunity wonOutcome wasn't stamped,
    // count finalized jobs as net for channel reporting (de-duped against net-won opp links).
    if (isFinalizedJob(job) && !netWonJobIds.has(job.id)) {
      bucket.wonGross += 1;
      bucket.wonNet += 1;
      netWonJobIds.add(job.id);
    }
  }

  // 5. Lead Integrity attribution
  const intakeByChannel = new Map<string, { valid: number; total: number; outOfArea: number }>();
  for (const rec of intakeRecords) {
    const id = resolveChannelId(rec.normalizedData?.leadSource) ?? "unattributed";
    if (!intakeByChannel.has(id)) intakeByChannel.set(id, { valid: 0, total: 0, outOfArea: 0 });
    const agg = intakeByChannel.get(id)!;
    agg.total += 1;
    if (rec.disposition === "approved" || rec.disposition === "approved_with_warning") agg.valid += 1;
    if (rec.geoResult === "out_of_area") agg.outOfArea += 1;
  }

  // 6. Finalize each bucket
  const rollups: ChannelRollup[] = [];
  for (const [id, b] of buckets) {
    b.sources = Array.from(sourcesByChannel.get(id) ?? []).sort();

    const intake = intakeByChannel.get(id);
    if (intake && intake.total > 0) {
      b.intakeCount = intake.total;
      b.validLeadPct = Math.round((intake.valid / intake.total) * 100);
      b.outOfAreaPct = Math.round((intake.outOfArea / intake.total) * 100);
    } else {
      // Sensible defaults when no intake records joined yet
      b.intakeCount = 0;
      b.validLeadPct = b.leads > 0 ? 75 : 0;
      b.outOfAreaPct = 0;
    }

    b.cpl          = b.leads > 0          ? Math.round(b.monthlySpend / b.leads)         : 0;
    b.cpb          = b.appointments > 0   ? Math.round(b.monthlySpend / b.appointments)  : 0;
    b.cpaGross     = b.wonGross > 0       ? Math.round(b.monthlySpend / b.wonGross)      : 0;
    b.cpaNet       = b.wonNet > 0         ? Math.round(b.monthlySpend / b.wonNet)        : 0;
    b.netConversionPct = b.leads > 0      ? (b.wonNet / b.leads) * 100                   : 0;
    b.roi          = b.monthlySpend > 0   ? ((b.revenue - b.monthlySpend) / b.monthlySpend) * 100 : 0;

    rollups.push(b);
  }

  // Order: managed → tracked → coming_soon → unattributed (within tier, by revenue desc)
  const tierOrder: Record<string, number> = { managed: 0, tracked: 1, coming_soon: 2, derived: 3 };
  return rollups.sort((a, b) => {
    const t = tierOrder[a.type] - tierOrder[b.type];
    if (t !== 0) return t;
    return b.revenue - a.revenue;
  });
}

// ── Aggregate summary ──────────────────────────────────────────
export interface ChannelSummary {
  totalSpend: number;
  totalLeads: number;
  totalNetJobs: number;
  totalRevenue: number;
  blendedCpaNet: number;
  blendedCpl: number;
  channelsActive: number;
  channelsTotal: number;
  unattributedLeads: number;
  unattributedPct: number;
}

export function summarize(rollups: ChannelRollup[]): ChannelSummary {
  const tracked = rollups.filter(r => r.id !== "unattributed");
  const totalSpend = tracked.reduce((s, r) => s + r.monthlySpend, 0);
  const totalLeads = rollups.reduce((s, r) => s + r.leads, 0);
  const totalNetJobs = rollups.reduce((s, r) => s + r.wonNet, 0);
  const totalRevenue = rollups.reduce((s, r) => s + r.revenue, 0);
  const unatt = rollups.find(r => r.id === "unattributed");
  return {
    totalSpend,
    totalLeads,
    totalNetJobs,
    totalRevenue,
    blendedCpaNet: totalNetJobs > 0 ? Math.round(totalSpend / totalNetJobs) : 0,
    blendedCpl: totalLeads > 0 ? Math.round(totalSpend / totalLeads) : 0,
    channelsActive: tracked.filter(r => r.active && r.leads > 0).length,
    channelsTotal: tracked.length,
    unattributedLeads: unatt?.leads ?? 0,
    unattributedPct: totalLeads > 0 ? Math.round(((unatt?.leads ?? 0) / totalLeads) * 100) : 0,
  };
}

// ── Health classification (single rule shared with Marketing Attribution) ──
export type ChannelHealth = "healthy" | "attention" | "underperforming" | "paused" | "no-data";

export function classifyHealth(r: ChannelRollup): { status: ChannelHealth; label: string } {
  if (!r.active) return { status: "paused", label: "Paused" };
  if (r.leads === 0) return { status: "no-data", label: "No data yet" };
  if (r.cpaNet > 0 && r.cpaNet > 500) return { status: "underperforming", label: "Underperforming" };
  if (r.netConversionPct < 10) return { status: "underperforming", label: "Underperforming" };
  if (r.cpaNet > 350 || r.validLeadPct < 60) return { status: "attention", label: "Needs attention" };
  return { status: "healthy", label: "Healthy" };
}
